import { useEffect, useId, useRef, useState } from 'react';

import {
  CODE_FONTS,
  DEFAULT_TEXT_SIZE,
  TEXT_SIZES,
  UI_FONTS,
  setTypography,
  useTypography,
  type FontChoice,
} from '../lib/typography.ts';
import { TypeIcon } from './icons.tsx';

/**
 * The interface font, code font and text size, from the foot of the rail beside the colour
 * theme. All three are saved in this browser (lib/typography.ts); the panel opens beside the
 * rail and closes on Escape or a click outside.
 */
export function FontPicker() {
  const [open, setOpen] = useState(false);
  const current = useTypography();
  const panelId = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (anchor.current && !anchor.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="theme-picker font-picker" ref={anchor}>
      <button
        ref={trigger}
        type="button"
        className="nav-item nav-rail-item"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
      >
        <TypeIcon size={16} />
        <span>Font</span>
      </button>
      {open ? (
        <div className="theme-panel font-panel" id={panelId} role="dialog" aria-label="Fonts and text size">
          <div className="theme-panel-head">
            <strong>Fonts and text size</strong>
            <span>Saved in this browser</span>
          </div>

          <div className="font-section">
            <div className="font-section-head">Text size</div>
            <div className="font-sizes" role="radiogroup" aria-label="Text size">
              {TEXT_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  role="radio"
                  aria-checked={size === current.textSize}
                  aria-label={`${size} pixels${size === DEFAULT_TEXT_SIZE ? ', default' : ''}`}
                  className="font-size-option"
                  onClick={() => setTypography({ ...current, textSize: size })}
                >
                  {size}
                </button>
              ))}
            </div>
            <p className="font-section-note">
              Body text in pixels; {DEFAULT_TEXT_SIZE} is the default. Settings → Display still scales the whole page.
            </p>
          </div>

          <FontList
            title="Interface font"
            fonts={UI_FONTS}
            selected={current.fontId}
            onPick={(fontId) => setTypography({ ...current, fontId })}
          />
          <FontList
            title="Code font"
            fonts={CODE_FONTS}
            selected={current.codeFontId}
            sample="0O 1lI"
            onPick={(codeFontId) => setTypography({ ...current, codeFontId })}
          />
        </div>
      ) : null}
    </div>
  );
}

interface FontListProps {
  title: string;
  fonts: readonly FontChoice[];
  selected: string;
  /** Shown in each font beside its name; the name alone when absent. */
  sample?: string;
  onPick: (id: string) => void;
}

/** One radio group of fonts, each name drawn in its own face. */
function FontList({ title, fonts, selected, sample, onPick }: FontListProps) {
  return (
    <div className="font-section">
      <div className="font-section-head">{title}</div>
      <div className="theme-list" role="radiogroup" aria-label={title}>
        {fonts.map((font) => (
          <button
            key={font.id}
            type="button"
            role="radio"
            aria-checked={font.id === selected}
            className="theme-option font-option"
            onClick={() => onPick(font.id)}
          >
            <span className="theme-option-text">
              <span className="theme-option-name" style={{ fontFamily: font.stack }}>
                {font.name}
                {sample ? <span className="font-option-sample"> {sample}</span> : null}
              </span>
              <span className="theme-option-description">{font.description}</span>
            </span>
            {font.id === selected ? <CheckMark /> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckMark() {
  return (
    <svg className="theme-option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}
